import React, { useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Avatar,
  useColorModeValue,
  keyframes,
  Flex,
  Center,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

interface ChatMessageProps {
  message: {
    role: 'user' | 'assistant' | 'system';
    content: string;
  };
  isLast?: boolean;
}

const MotionBox = motion.create(Box);

export function ChatMessage({ message, isLast = false }: ChatMessageProps) {
  const isUser = message.role === 'user';

  // Color mode values
  const userBgColor = useColorModeValue('blue.500', 'blue.400');
  const assistantBgColor = useColorModeValue('gray.100', 'gray.700');
  const userTextColor = 'white';
  const assistantTextColor = useColorModeValue('gray.800', 'white');

  // Determine emoji based on message content
  const assistantEmoji = useMemo(() => {
    if (!isUser) {
      const content = message.content.toLowerCase();

      // Check for positive content
      if (content.includes('great job') ||
        content.includes('excellent') ||
        content.includes('well done') ||
        content.includes('perfect')) {
        return '😄'; // Excited emoji
      }

      // Check for corrective content
      if (content.includes('incorrect') ||
        content.includes('error') ||
        content.includes('mistake') ||
        content.includes('not quite')) {
        return '🤔'; // Thinking emoji
      }

      // Check for congratulatory content
      if (content.includes('congratulations') ||
        content.includes('amazing') ||
        content.includes('impressive')) {
        return '🎉'; // Celebration emoji
      }

      // Check for grammar corrections
      if (content.includes('grammar') ||
        content.includes('correction') ||
        content.includes('should be')) {
        return '📝'; // Pencil emoji
      }

      // Default happy teacher
      return '👨‍🏫';
    }
    return '';
  }, [isUser, message.content]);

  // Background color for emoji avatar
  const emojiBgColor = useColorModeValue('purple.100', 'purple.800');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      width="100%"
    >
      <HStack
        alignItems="start"
        spacing={2}
        justify={isUser ? 'flex-end' : 'flex-start'}
        width="100%"
        mb={4}
      >
        {!isUser && (
          <Flex
            justifyContent="center"
            alignItems="center"
            width="32px"
            height="32px"
            borderRadius="full"
            bg={emojiBgColor}
            fontSize="xl"
          >
            {assistantEmoji}
          </Flex>
        )}
        <VStack
          align={isUser ? 'flex-end' : 'flex-start'}
          maxW="80%"
          spacing={1}
        >
          <Box
            bg={isUser ? userBgColor : assistantBgColor}
            color={isUser ? userTextColor : assistantTextColor}
            px={4}
            py={2}
            borderRadius="lg"
            shadow="sm"
          >
            <Text fontSize="md">{message.content}</Text>
          </Box>
        </VStack>
        {isUser && (
          <Avatar
            size="sm"
            name="User"
            src="/user-avatar.png"
            bg="gray.500"
          />
        )}
      </HStack>
    </MotionBox>
  );
}

export default ChatMessage;
export type { ChatMessageProps };
